import { useState, useMemo, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import "../styles/TracesExceptionsLayout.css";
import CustomDateRangeSelector from "../components/common/CustomDateRangeFilter/CustomDateRangeSelector";
import Dropdown from "../components/common/Dropdown";
import CustomButton from "../components/common/CustomButton/CustomButton";
import { getAuthToken } from "../api/api";
import { AgGridReact } from "ag-grid-react"; // the AG Grid React Component
import { getAPIUrl, suppressKeyboardEvent, notify } from "../components/common/helpers";
import "ag-grid-community/styles/ag-grid.css"; // Core grid CSS, always needed
import "ag-grid-community/styles/ag-theme-alpine.css"; // Optional theme CSS

const pageConfig = {
  "traces-exceptions/traces": {
    breadcrumb: "traces-exceptions/traces",
    dropdowns: [
      { name: "Event Page", options: ["Firmware Store", "Site Details", "Release Management", "User Management", "Contract Management"] },
    ],
    columnDefs: [
      {
        headerName: "Event Page",
        field: "EventPage",
        floatingFilter: true,
      },
      {
        headerName: "Event Type",
        field: "eventType",
        floatingFilter: true,
      },
      {
        headerName: "Message",
        field: "message",
        floatingFilter: true,
      },
      {
        headerName: "Created By",
        field: "userId",
        floatingFilter: true,
      },
      {
        headerName: "Timestamp",
        field: "TimeStamp",
        floatingFilter: true,
      },
    ],
  },
  "traces-exceptions/exceptions": {
    breadcrumb: "traces-exceptions/exceptions",
    dropdowns: [
      { name: "Event Page", options: ["Firmware Store", "Site Details", "Release Management", "User Management", "Contract Management"] },
    ],
    columnDefs: [
      {
        headerName: "Event Page",
        field: "EventPage",
        floatingFilter: true,
      },
      {
        headerName: "Event Type",
        field: "eventType",
        floatingFilter: true,
      },
      {
        headerName: "Message",
        field: "message",
        floatingFilter: true,
      },
      {
        headerName: "Exception Details",
        field: "exception",
        floatingFilter: true,
      },
      {
        headerName: "Created By",
        field: "userId",
        floatingFilter: true,
      },
      {
        headerName: "Timestamp",
        field: "TimeStamp",
        floatingFilter: true,
      },
    ],
  },
};

export default function TracesExceptionsDashboard() {
  const location = useLocation();
  const pageKey = useMemo(() => {
    const path = location.pathname.replace("/", "").toLowerCase();
    return pageConfig[path] ? path : "traces-exceptions/traces";
  }, [location.pathname]);

  const page = pageConfig[pageKey];
  const isClearingRef = useRef(false);
  const [selectedFirst, setSelectedFirst] = useState("");
  const [dateRange, setDateRange] = useState(null);
  const [gridApi, setGridApi] = useState(null);
  const [columnApi, setColumnApi] = useState(null);
  const [resetDateRange, setResetDateRange] = useState(0);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [hasGridState, setHasGridState] = useState(false);
  const appliedFilterRef = useRef(null);

const currentFilter = JSON.stringify({
  startDate: dateRange?.startDate ?? null,
  endDate: dateRange?.endDate ?? null,
  eventPagescr: selectedFirst || null,
});

const onFilterChanged = () => {
  if (!gridApi) return;

  const filterApplied =
    Object.keys(gridApi.getFilterModel() || {}).length > 0;

  const sortApplied =
    columnApi
      ?.getColumnState()
      ?.some((column) => Boolean(column.sort)) ?? false;

  setHasGridState(filterApplied || sortApplied);
};

const onSortChanged = () => {
  if (!gridApi) return;

  const filterApplied =
    Object.keys(gridApi.getFilterModel() || {}).length > 0;

  const sortApplied =
    columnApi
      ?.getColumnState()
      ?.some((column) => Boolean(column.sort)) ?? false;

  setHasGridState(filterApplied || sortApplied);
};

const isApplyDisabled =
  (!dateRange && !selectedFirst) ||
  currentFilter === selectedFilter;

  const controller = new AbortController();

  const onGridReady = (params) => {
    setGridApi(params.api);
    setColumnApi(params.columnApi);
  };

  const handleDateRangeSelect = (dates) => {
    setDateRange(dates);
  };

  const showErrorOverlay = (gridApi) => {
    if (gridApi) {
      gridApi.showNoRowsOverlay();
    }
  };

  const handleApiError = (gridApi) => {
    showErrorOverlay(gridApi);
  };

  const defaultColDef = {
    sortable: true,
    editable: true,
    filter: true,
    flex: 1,
    resizable: true,
    minWidth: 110,
    suppressKeyboardEvent,
  };

  const resetFilters = () => {
    setSelectedFirst("");
    setDateRange(null);
    setResetDateRange((prev) => prev + 1);
  }

  useEffect(() => {
  if (!gridApi) return;

  gridApi.setFilterModel(null);

  columnApi?.applyColumnState({
    defaultState: {
      sort: null,
    },
  });
  setHasGridState(false);
  getLogsDetails(null);
  resetFilters();
}, [gridApi, pageKey]);

  // Grid parameters formation for API call
  const createServerRequest = (params) => {
    const { startRow, endRow, filterModel, sortModel } = params;
    let sortQuery = '';
    let filterParams = '';
    if (sortModel && sortModel.length > 0 && sortModel !== undefined) {
      sortQuery = `&sortColumn=${sortModel[0].colId}&sortOrder=${sortModel[0].sort}`;
    }
    if (Object.keys(filterModel).length > 0) {
      filterParams = Object.keys(filterModel).map(field => {
        const filter = filterModel[field];
        return `&${field}=${filter.filter}`
      }).join('');
    }
    return `?filter_page=${startRow}&filter_pageSize=${endRow}${filterParams}${sortQuery}`;
  }

  // level parameter formation for route specific API differentiation
  const levelParameters = () => {
    let levelParams = '';
    if (pageKey === 'traces-exceptions/exceptions') {
      // send plain value (no quotes) so the backend receives level=Error
      levelParams = `&level=Error`;
    } return levelParams;
  }

  // Filter parameter formation for applied filters
  const filterParameters = (filterSource = selectedFilter) => {
    if (!filterSource) return '';

    const filterObj = typeof filterSource === 'string'
      ? JSON.parse(filterSource)
      : filterSource;

    const params = [];
    if (filterObj.startDate) params.push(`startDate=${filterObj.startDate}`);
    if (filterObj.endDate) params.push(`endDate=${filterObj.endDate}`);
    if (filterObj.eventPagescr) params.push(`eventPagescr=${filterObj.eventPagescr}`);

    return params.length ? `&${params.join('&')}` : '';
  }

  const getLogsDetails = () => {
  if (!gridApi) return;

  const dataSource = {
    getRows: (params) => {
      const requestParameters = createServerRequest(params);
      const levelParams = levelParameters();

      // Always reads the latest applied global filter
      const globalFilterParams = filterParameters(
        appliedFilterRef.current
      );

      gridApi.showLoadingOverlay();

      axios({
        method: "GET",
        url:
          getAPIUrl("getLogsAndTracesListApi") +
          requestParameters +
          levelParams +
          globalFilterParams,
        headers: {
          Authorization: getAuthToken(),
        },
        signal: controller.signal,
      })
        .then((res) => {
          if (res?.data?.traces?.length > 0) {
            params.successCallback(
              res.data.traces,
              res.data.lastRow
            );

            gridApi.hideOverlay();
          } else {
            params.successCallback([], 0);
            handleApiError(gridApi);
          }
        })
        .catch((err) => {
          console.log(err);

          params.failCallback();
          handleApiError(gridApi);

          if (err.message !== "canceled") {
            notify("fetching");
          }
        });
    },
  };

  gridApi.setDatasource(dataSource);
};

 
  const handleApply = () => {
  const filterObj = {
    startDate: dateRange?.startDate ?? null,
    endDate: dateRange?.endDate ?? null,
    eventPagescr: selectedFirst || null,
  };

  const filterBody = JSON.stringify(filterObj);

  appliedFilterRef.current = filterBody;
  setSelectedFilter(filterBody);

  gridApi?.paginationGoToFirstPage();
  gridApi?.purgeInfiniteCache();
};

  const isClearDisabled =
  !dateRange &&
  !selectedFirst &&
  !hasGridState;

const handleClear = () => {
  if (!gridApi) return;

  appliedFilterRef.current = null;
  isClearingRef.current = true;

  resetFilters();
  setSelectedFilter(null);
  setHasGridState(false);
  gridApi.setFilterModel(null);
  // gridApi.paginationGoToFirstPage();
  getLogsDetails();

  isClearingRef.current = false;
};
  return (
    <div className="traces-exceptions-page">
      <div className="traces-exceptions-header">
        <div className="traces-exceptions-title">
          <span>Logs/Traces</span>
        </div>
        <div
          className="container-fluid"
          style={{
            background: "#1F1F1F 0% 0% no-repeat padding-box",
            border: "1px solid #707070",
            width: "100%",
            padding: "10px",
          }}
        >
          <div className="traces-exceptions-breadcrumb">{page.breadcrumb.split("/")[1].toLowerCase().replace(/\b\w/g, (l) => l.toUpperCase())}</div>

          <div className="filter-panel">
            <div className="filter-row">

              <CustomDateRangeSelector
                onDateRangeSelect={handleDateRangeSelect}
                resetTrigger={resetDateRange} />
              <div className="filter-field">
                <Dropdown
                  label={page.dropdowns[0].name}
                  options={page.dropdowns[0].options}
                  value={selectedFirst}
                  onChange={(value) => {
                    setSelectedFirst(value);
                  }}
                  name="dropdown-first"
                />
              </div>
              <div className="apply-button">
                <CustomButton
                  buttonStyle="white"
                  label="Apply"
                  handleClick={handleApply}
                  disabled={isApplyDisabled}
                />
                <CustomButton
                  buttonStyle="white"
                  label="Reset All"
                  handleClick={handleClear}
                  disabled={isClearDisabled}
                />
              </div>

            </div>
          </div>

          <div
            className="ag-theme-alpine-dark"
            style={{ height: 400, width: "100%" }}
          >
            <AgGridReact
              onGridReady={onGridReady}
              rowModelType={"infinite"}
              onFilterChanged={onFilterChanged}
              onSortChanged={onSortChanged}
              columnDefs={page.columnDefs}
              defaultColDef={defaultColDef}
              pagination={true}
              paginationPageSize={10}
              cacheBlockSize={10}
              filter={true}
              sortable={true}
            ></AgGridReact>
          </div>
        </div>
      </div>
    </div>
  );
}
